import  { useState } from 'react';
import { FileText, Edit, Trash2, Eye, Download, Send, Filter, Search, Plus, Copy, Printer } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import { Invoice } from '../types';
import toast from 'react-hot-toast';
import { SEO } from './shared/SEO';
import { useAuth } from '../contexts/AuthContext';
import { generateInvoicePDF } from '../utils/pdfGenerator';
import { emailService } from '../services/emailService';
import { ref, getDownloadURL } from 'firebase/storage';
import { storage } from '../lib/firebase';


export function InvoiceList() {
  const { invoices, deleteInvoice, sendInvoice, createInvoice, getNextInvoiceNumber } = useApp();
  const { currentWorkspace } = useAuth();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [actionLoading, setActionLoading] = useState<{ [invoiceId: string]: string | null }>({});
  const [actionSuccess, setActionSuccess] = useState<{ [invoiceId: string]: string | null }>({});

  const filteredInvoices = invoices.filter(invoice => {
    const matchesSearch = 
      invoice.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.client.name.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || invoice.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const handleDelete = async (invoiceId: string) => {
    if (window.confirm('Are you sure you want to delete this invoice?')) {
      try {
        await deleteInvoice(invoiceId);
        toast.success('Invoice deleted successfully!');
      } catch (error) {
        console.error('Error deleting invoice:', error);
        toast.error('Failed to delete invoice. Please try again.');
      }
    }
  };


  const handleView = (invoice: Invoice) => {
    navigate(`/app/invoices/${invoice.id}`);
  };

  const handleEdit = (invoice: Invoice) => {
    navigate(`/app/invoices/${invoice.id}/edit`);
  };

  const handleDuplicate = async (invoice: Invoice) => {
    try {
      // Generate a new invoice number for the duplicate
      const nextInvoiceNumber = getNextInvoiceNumber();

      const duplicateInvoiceData = {
        clientId: invoice.clientId,
        client: invoice.client,
        invoiceNumber: nextInvoiceNumber,
        issueDate: new Date().toISOString().split('T')[0],
        dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        status: 'draft' as const,
        items: invoice.items,
        subtotal: invoice.subtotal,
        taxRate: invoice.taxRate,
        taxAmount: invoice.taxAmount,
        discountAmount: invoice.discountAmount,
        total: invoice.total,
        notes: invoice.notes,
        terms: invoice.terms,
        paymentLink: invoice.paymentLink,
        paymentStatus: 'pending' as const
      };

      await createInvoice(duplicateInvoiceData);
      toast.success('Invoice duplicated successfully!');
    } catch (error) {
      console.error('Error duplicating invoice:', error);
      toast.error('Failed to duplicate invoice. Please try again.');
    }
  };

  // --- InvoiceTemplate-style actions ---
  const handleDownloadPDF = async (invoice: Invoice) => {
    if (!currentWorkspace) return;
    setActionLoading(prev => ({ ...prev, [invoice.id]: 'download' }));
    try {
      const workspaceWithLogoAndCurrency = {
        ...currentWorkspace,
        logo: currentWorkspace.settings?.branding?.logo,
        settings: {
          ...currentWorkspace.settings,
          currency: invoice.currency || currentWorkspace.settings?.currency || 'USD'
        }
      };
      await generateInvoicePDF(invoice, workspaceWithLogoAndCurrency);
      setActionSuccess(prev => ({ ...prev, [invoice.id]: 'download' }));
      setTimeout(() => setActionSuccess(prev => ({ ...prev, [invoice.id]: null })), 2000);
    } catch (error) {
      toast.error('Error generating PDF. Please try again.');
    } finally {
      setActionLoading(prev => ({ ...prev, [invoice.id]: null }));
    }
  };

  const handlePrint = async (invoice: Invoice) => {
    // Open invoice in print mode in a new window
    const printWindow = window.open(`/app/invoices/${invoice.id}?print=true`, '_blank');
    if (printWindow) {
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 1000);
      };
    }
  };

  const handleSendEmail = async (invoice: Invoice) => {
    if (!currentWorkspace || !invoice.client.email) return;
    if (!currentWorkspace.settings?.resendApiKey) {
      alert('Please configure your Resend API key in workspace settings before sending emails.');
      return;
    }
    setActionLoading(prev => ({ ...prev, [invoice.id]: 'send' }));
    try {
      const success = await emailService.sendInvoiceEmail(invoice, currentWorkspace);
      if (success) {
        setActionSuccess(prev => ({ ...prev, [invoice.id]: 'send' }));
        toast.success('Invoice email sent successfully!');
        setTimeout(() => setActionSuccess(prev => ({ ...prev, [invoice.id]: null })), 2000);
      } else {
        alert('Failed to send email. Please check your email configuration in workspace settings.');
      }
    } catch (error) {
      toast.error('Failed to send email. Please check your email configuration and try again.');
    } finally {
      setActionLoading(prev => ({ ...prev, [invoice.id]: null }));
    }
  };

  const handleCopyDownloadLink = async (invoice: Invoice) => {
    if (!currentWorkspace || !invoice) return;
    setActionLoading(prev => ({ ...prev, [invoice.id]: 'copy' }));
    try {
      const safeClientName = invoice.client?.name
        ?.toLowerCase()
        .replace(/[^a-z0-9]/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '') || 'unnamed_client';
      const fileName = `invoices/${currentWorkspace.name}/${safeClientName}/Invoice_${invoice.invoiceNumber}.pdf`;
      const storageRef = ref(storage, fileName);
      const downloadUrl = await getDownloadURL(storageRef);
      await navigator.clipboard.writeText(downloadUrl);
      setActionSuccess(prev => ({ ...prev, [invoice.id]: 'copy' }));
      toast.success('Download link copied!');
      setTimeout(() => setActionSuccess(prev => ({ ...prev, [invoice.id]: null })), 2000);
    } catch (err) {
      toast.error('Could not copy download link. Make sure the invoice PDF has been generated.');
    } finally {
      setActionLoading(prev => ({ ...prev, [invoice.id]: null }));
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'sent':
        return 'bg-blue-100 text-blue-800';
      case 'overdue':
        return 'bg-red-100 text-red-800';
      case 'viewed':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Currency formatting logic matching InvoiceTemplate
  const getCurrencySymbol = (curr: string): string => {
    const symbols: { [key: string]: string } = {
      'USD': '$', 'EUR': '€', 'GBP': '£', 'QAR': 'QAR ', 'AED': 'AED ', 'SAR': 'SAR ', 'INR': '₹', 'JPY': '¥', 'CNY': '¥', 'AUD': 'A$', 'CAD': 'C$', 'NZD': 'NZ$', 'SGD': 'S$', 'HKD': 'HK$', 'KRW': '₩', 'BRL': 'R$', 'ZAR': 'R', 'RUB': '₽', 'CHF': 'CHF ', 'SEK': 'kr ', 'NOK': 'kr ', 'DKK': 'kr ', 'PLN': 'zł', 'TRY': '₺', 'MXN': 'Mex$', 'IDR': 'Rp ', 'MYR': 'RM ', 'PHP': '₱', 'THB': '฿', 'VND': '₫', 'EGP': 'E£', 'PKR': '₨', 'BDT': '৳', 'NGN': '₦', 'GHS': 'GH₵', 'KES': 'KSh ', 'UGX': 'USh ', 'TZS': 'TSh ', 'RWF': 'RF ', 'BIF': 'FBu ', 'ETB': 'Br ', 'XOF': 'CFA ', 'XAF': 'FCFA ',
    };
    return symbols[curr] || `${curr} `;
  };
  const formatCurrency = (amount: number, currency: string) => {
    const symbol = getCurrencySymbol(currency);
    let formattedAmount = amount.toFixed(2);
    switch(currency) {
      case 'JPY': case 'KRW': case 'VND':
        formattedAmount = Math.round(amount).toString();
        break;
      case 'BHD': case 'KWD': case 'OMR':
        formattedAmount = amount.toFixed(3);
        break;
    }
    formattedAmount = formattedAmount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const symbolAfter = ['kr', 'Kč', 'Ft'].some(s => symbol.trim() === s);
    return symbolAfter ? `${formattedAmount} ${symbol}` : `${symbol}${formattedAmount}`;
  };

  return (
    <>
      <SEO
        title="Invoices"
        description="View, manage, and send invoices for your freelance business with FreelancePay."
        keywords={["invoices", "invoice management", "freelance", "FreelancePay"]}
      />
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Invoices</h2>
            <p className="text-gray-600">Manage and track your invoices</p>
          </div>
          <button 
            onClick={() => navigate('/app/invoices/new')}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Invoice
          </button>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search invoices..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
            <div className="flex items-center space-x-3">
              <Filter className="h-4 w-4 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              >
                <option value="all">All Status</option>
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="viewed">Viewed</option>
                <option value="paid">Paid</option>
                <option value="overdue">Overdue</option>
              </select>
            </div>
          </div>
        </div>

        {filteredInvoices.length > 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Invoice
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Client
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Due Date
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredInvoices.map((invoice) => (
                    <tr key={invoice.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="p-2 bg-green-100 rounded-lg mr-3">
                            <FileText className="h-4 w-4 text-green-600" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              #{invoice.invoiceNumber}
                            </div>
                            <div className="text-sm text-gray-500">
                              {new Date(invoice.issueDate).toLocaleDateString()}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{invoice.client.name}</div>
                        <div className="text-sm text-gray-500">{invoice.client.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {invoice.currency ? formatCurrency(invoice.total, invoice.currency) : invoice.total.toLocaleString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(invoice.status)}`}>
                          {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(invoice.dueDate).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleView(invoice)}
                            className="text-gray-400 hover:text-green-600 transition-colors p-1"
                            title="View Invoice"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleEdit(invoice)}
                            className="text-gray-400 hover:text-blue-600 transition-colors p-1"
                            title="Edit Invoice"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDuplicate(invoice)}
                            className="text-gray-400 hover:text-indigo-600 transition-colors p-1"
                            title="Duplicate Invoice"
                          >
                            <Copy className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDownloadPDF(invoice)}
                            className={`text-gray-400 hover:text-purple-600 transition-colors p-1 ${actionLoading[invoice.id]==='download' ? 'opacity-50 cursor-not-allowed' : ''}`}
                            title="Download PDF"
                            disabled={actionLoading[invoice.id]==='download'}
                          >
                            <Download className="h-4 w-4" />
                            {actionSuccess[invoice.id]==='download' && <span className="ml-1 text-xs text-green-600">Done!</span>}
                          </button>
                          <button
                            onClick={() => handlePrint(invoice)}
                            className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                            title="Print Invoice"
                          >
                            <Printer className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleSendEmail(invoice)}
                            className={`text-gray-400 hover:text-blue-600 transition-colors p-1 ${actionLoading[invoice.id]==='send' ? 'opacity-50 cursor-not-allowed' : ''}`}
                            title="Send Email"
                            disabled={actionLoading[invoice.id]==='send'}
                          >
                            <Send className="h-4 w-4" />
                            {actionSuccess[invoice.id]==='send' && <span className="ml-1 text-xs text-green-600">Sent!</span>}
                          </button>
                          <button
                            onClick={() => handleCopyDownloadLink(invoice)}
                            className={`text-gray-400 hover:text-gray-600 transition-colors p-1 ${actionLoading[invoice.id]==='copy' ? 'opacity-50 cursor-not-allowed' : ''}`}
                            title="Copy Download Link"
                            disabled={actionLoading[invoice.id]==='copy'}
                          >
                            <Copy className="h-4 w-4" />
                            {actionSuccess[invoice.id]==='copy' && <span className="ml-1 text-xs text-green-600">Copied!</span>}
                          </button>
                          <button
                            onClick={() => handleDelete(invoice.id)}
                            className="text-gray-400 hover:text-red-600 transition-colors p-1"
                            title="Delete Invoice"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchTerm || statusFilter !== 'all' ? 'No invoices found' : 'No invoices yet'}
            </h3>
            <p className="text-gray-500 mb-6">
              {searchTerm || statusFilter !== 'all' 
                ? 'Try adjusting your search or filter criteria'
                : 'Get started by creating your first invoice'
              }
            </p>
            {!searchTerm && statusFilter === 'all' && (
              <button 
                onClick={() => navigate('/app/invoices/new')}
                className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Invoice
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
}